import { find } from "lodash";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import type { UUID } from "crypto";
import { getNextPlayableProperty, getPuzzleFeed } from "@/services/PropertyService";
import {
    getPuzzleFeedQueryKey,
    PUZZLE_FEED_NEXT_MAX_PAGES,
    PUZZLE_FEED_PAGE_LIMIT,
} from "@/constants/puzzle-feed.constants";

/** Max API round-trips when skipping client-invalid playable properties (registered users). */
const AUTH_NEXT_PROPERTY_MAX_ATTEMPTS = 25;

/** Feed/API item shape for "next property" (item.id is playable property ID). Optional fields used by modal UI. */
export type FeedItemForNext = {
    id?: string;
    shortLink?: string | null;
    longLink?: string | null;
    imageIndex?: number;
    property?: { id?: string; city?: string; state?: string; [key: string]: unknown };
};

/** Matches structure stored in localStorage under "guessed_property_results" (Puzzle index.tsx) */
export type PuzzleGuessedPropertyResult = {
    playablePropertyId: string;
    property_play_date?: string;
    property: { id?: string; [key: string]: unknown };
    guesses: unknown[];
    currentIndex: number;
    version: string;
};

type PuzzleFeedPageResponse = Awaited<ReturnType<typeof getPuzzleFeed>>;

export type FetchNextPlayablePropertyParams = {
    /** When set, uses GET /api/playable-properties/next/:userId (registered users). */
    userId?: string;
    /** Guest feed: passed to GET /api/feeds/puzzle as userId / requestingUserId. */
    requestingUserId?: string;
    /** Guest feed: sort cursor from URL (?slc=), same as PuzzleFeed page 1. */
    slc?: string | null;
    /** Guest feed: scans cached infinite-query pages before fetching more batches. */
    queryClient?: QueryClient;
    currentPlayablePropertyId: string | number | null;
    currentPropertyId: string | undefined;
    guessedPropertyIds: string[];
    guessedPropertyResults: PuzzleGuessedPropertyResult[];
    versionStored: string | undefined;
    /** Playable property IDs to skip (e.g. listings that failed client-side image validation). */
    excludedPlayablePropertyIds?: string[];
};

function buildExcludedPlayableIdSet(
    currentPlayablePropertyId: string | number | null,
    excludedPlayablePropertyIds: string[] | undefined,
): Set<string> {
    const excluded = new Set<string>();
    if (currentPlayablePropertyId != null && currentPlayablePropertyId !== "") {
        excluded.add(String(currentPlayablePropertyId));
    }
    (excludedPlayablePropertyIds ?? []).forEach((id) => {
        if (id != null && String(id) !== "") {
            excluded.add(String(id));
        }
    });
    return excluded;
}

function buildIsItemEligible(
    params: FetchNextPlayablePropertyParams,
    excludedPlayableIds: Set<string>,
): (item: FeedItemForNext) => boolean {
    const guessedIds = new Set(params.guessedPropertyIds.map(String));
    const { currentPropertyId, guessedPropertyResults, versionStored } = params;
    return (item: FeedItemForNext): boolean => {
        const playableId = item?.id;
        if (!playableId) {
            return false;
        }
        const playableIdStr = String(playableId);
        if (excludedPlayableIds.has(playableIdStr) || item?.property?.id === currentPropertyId) {
            return false;
        }
        if (guessedIds.has(playableIdStr)) {
            return false;
        }
        const guessedResult = find(
            guessedPropertyResults,
            (r) => String(r.playablePropertyId) === playableIdStr && r.version === versionStored,
        );
        return !guessedResult;
    };
}

function findEligibleInFeedPages(
    pages: PuzzleFeedPageResponse[],
    isItemEligible: (item: FeedItemForNext) => boolean,
): FeedItemForNext | null {
    for (const page of pages) {
        const feedItems: FeedItemForNext[] = page?.data?.data ?? [];
        const nextProperty = feedItems.find(isItemEligible);
        if (nextProperty) {
            return nextProperty;
        }
    }
    return null;
}

function appendPuzzleFeedPageToCache(
    queryClient: QueryClient | undefined,
    queryKey: ReturnType<typeof getPuzzleFeedQueryKey>,
    pageData: PuzzleFeedPageResponse,
    pageNumber: number,
): void {
    if (!queryClient) {
        return;
    }
    queryClient.setQueryData<InfiniteData<PuzzleFeedPageResponse>>(queryKey, (old) => {
        if (!old) {
            return { pages: [pageData], pageParams: [pageNumber] };
        }
        if (old.pages.length >= pageNumber) {
            return old;
        }
        return {
            pages: [...old.pages, pageData],
            pageParams: [...old.pageParams, pageNumber],
        };
    });
}

/**
 * Registered users: next unplayed playable property from the backend (not feed order).
 */
async function fetchNextFromPlayablePropertyApi(
    params: FetchNextPlayablePropertyParams,
): Promise<FeedItemForNext | null> {
    if (!params.userId) {
        return null;
    }
    const excludedPlayableIds = buildExcludedPlayableIdSet(
        params.currentPlayablePropertyId,
        params.excludedPlayablePropertyIds,
    );
    let excludeId: string | number | null | undefined = params.currentPlayablePropertyId;
    for (let attempt = 0; attempt < AUTH_NEXT_PROPERTY_MAX_ATTEMPTS; attempt += 1) {
        const response = await getNextPlayableProperty(params.userId as UUID, excludeId ?? null);
        const nextItem = (response?.data as FeedItemForNext) || null;
        if (!nextItem?.id) {
            return null;
        }
        const playableIdStr = String(nextItem.id);
        if (!excludedPlayableIds.has(playableIdStr)) {
            return nextItem;
        }
        excludeId = nextItem.id;
    }
    return null;
}

/**
 * Guests: puzzle feed batches (cached infinite-query pages first, then new pages like scroll load-more).
 */
async function fetchNextFromPuzzleFeed(params: FetchNextPlayablePropertyParams): Promise<FeedItemForNext | null> {
    const requestingUserId = params.requestingUserId ?? params.userId;
    const queryKey = getPuzzleFeedQueryKey(requestingUserId, params.slc);
    const isItemEligible = buildIsItemEligible(
        params,
        buildExcludedPlayableIdSet(params.currentPlayablePropertyId, params.excludedPlayablePropertyIds),
    );
    const cached = params.queryClient?.getQueryData<InfiniteData<PuzzleFeedPageResponse>>(queryKey);
    const cachedPages = cached?.pages ?? [];
    if (cachedPages.length > 0) {
        const fromCache = findEligibleInFeedPages(cachedPages, isItemEligible);
        if (fromCache) {
            return fromCache;
        }
    }
    let page = cachedPages.length > 0 ? cachedPages.length + 1 : 1;
    let pagesFetched = 0;
    while (pagesFetched < PUZZLE_FEED_NEXT_MAX_PAGES) {
        const feedResponse = await getPuzzleFeed({
            page,
            limit: PUZZLE_FEED_PAGE_LIMIT,
            userId: requestingUserId,
            requestingUserId,
            slc: page === 1 ? params.slc : null,
        });
        appendPuzzleFeedPageToCache(params.queryClient, queryKey, feedResponse, page);
        const feedItems: FeedItemForNext[] = feedResponse?.data?.data ?? [];
        const nextProperty = feedItems.find(isItemEligible);
        if (nextProperty) {
            return nextProperty;
        }
        const hasNextPage = feedResponse?.data?.hasNextPage === true;
        if (!hasNextPage || feedItems.length < PUZZLE_FEED_PAGE_LIMIT) {
            return null;
        }
        page += 1;
        pagesFetched += 1;
    }
    return null;
}

/**
 * Fetches the next playable property: registered users via next-playable API; guests via puzzle feed.
 * Returns null if none found.
 */
export async function fetchNextPlayableProperty(
    params: FetchNextPlayablePropertyParams,
): Promise<FeedItemForNext | null> {
    try {
        if (params.userId) {
            return await fetchNextFromPlayablePropertyApi(params);
        }
        return await fetchNextFromPuzzleFeed(params);
    } catch (error) {
        if ((error as { response?: { status?: number } })?.response?.status === 404) {
            return null;
        }
        throw error;
    }
}

/**
 * Builds a puzzle listing route (guesses view) from a feed/API next-property item.
 */
export function buildPuzzleListingPathFromNextItem(item: FeedItemForNext): string | null {
    const longLink = item.longLink != null ? String(item.longLink).trim() : "";
    if (longLink) {
        return longLink.startsWith("/") ? `/puzzle${longLink}` : `/puzzle/${longLink}`;
    }
    const shortLink = item.shortLink != null ? String(item.shortLink).trim() : "";
    if (shortLink) {
        return `/puzzle/${shortLink}`;
    }
    return null;
}
